import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Activity, Cpu, ShieldCheck, Wallet, Target } from "lucide-react";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { Loading, Bar } from "@/pages/_shared";
import api from "@/lib/api";

function Kpi({ label, value, sub, i }) {
  return (
    <div className="kpi-lux animate-fade-up p-4" style={{ animationDelay: `${i * 70}ms` }} data-testid={`aiwf-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 font-mono text-2xl font-extrabold text-white">{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}

function Breakdown({ title, icon, rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card className="p-5">
      <SectionTitle icon={icon}>{title}</SectionTitle>
      {rows.length === 0 && <div className="text-[13px] text-slate-400">No runs recorded yet.</div>}
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center gap-3 text-[12px]">
            <span className="w-40 truncate font-medium text-slate-700">{r.key}</span>
            <Bar pct={(r.count / max) * 100} height={6} />
            <span className="w-8 text-right font-mono text-slate-500">{r.count}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function AIWorkforceCommand() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/owner/ai-workforce").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loading />;
  const maxDay = Math.max(1, ...d.daily.map((x) => x.count));
  const perms = Object.entries(d.action_permissions);
  return (
    <div className="animate-fade-up space-y-6" data-testid="ai-workforce-command-page">
      <PageHeader eyebrow="AI Workforce" title="AI Workforce Command"
        subtitle="Real operational telemetry: every agent run, mission, approval and action permission — aggregated from the platform's own records. Nothing simulated."
        actions={<Link to="/owner/ai-workforce/agents" data-testid="aiwf-open-agents" className="rounded-lg border border-slate-300 px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50">Open agent roster →</Link>} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi i={0} label="Total runs" value={d.runs.total} sub="all time" />
        <Kpi i={1} label="Today" value={d.runs.today} sub={`${d.runs.last_7d} in last 7 days`} />
        <Kpi i={2} label="Auto-pilot runs" value={d.runs.auto_pilot} sub="no human prompt" />
        <Kpi i={3} label="Approvals pending" value={d.approvals_pending} sub="human decisions" />
        <Kpi i={4} label="Est. AI cost" value={`$${d.runs.estimated_cost_usd}`} sub={`@ $${d.runs.rate_per_call}/call · estimate`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Breakdown title="Runs by agent" icon={Bot} rows={d.by_agent} />
        <Breakdown title="Runs by model" icon={Cpu} rows={d.by_model} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2" data-testid="aiwf-daily">
          <SectionTitle icon={Activity}>Runs — last 7 days</SectionTitle>
          {d.daily.length === 0 ? <div className="text-[13px] text-slate-400">No activity in the last 7 days.</div> : (
            <div className="flex items-end gap-2" style={{ height: 110 }}>
              {d.daily.map((x) => (
                <div key={x.day} className="flex flex-1 flex-col items-center gap-1" title={`${x.day}: ${x.count}`}>
                  <div className="w-full rounded-t bg-[#1a5fd0]/80" style={{ height: `${(x.count / maxDay) * 90}%`, minHeight: 3 }} />
                  <span className="font-mono text-[9px] text-slate-400">{x.day.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5" data-testid="aiwf-missions">
          <SectionTitle icon={Target}>Missions</SectionTitle>
          {Object.keys(d.missions).length === 0 && <div className="text-[13px] text-slate-400">No missions yet.</div>}
          {Object.entries(d.missions).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-[13px] last:border-0"><span className="capitalize text-slate-700">{k.replace(/_/g, " ")}</span><span className="font-mono text-slate-500">{v}</span></div>
          ))}
        </Card>
      </div>

      <Card className="p-5" data-testid="aiwf-permissions">
        <SectionTitle icon={ShieldCheck}>Action permissions (Operating Mode)</SectionTitle>
        {perms.length === 0 ? <div className="text-[13px] text-slate-400">No per-action permissions saved yet — defaults apply (high-risk actions require approval). <Link to="/owner/operating-mode" className="text-[#1a5fd0] underline">Configure</Link>.</div> : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {perms.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-[12px]"><span className="text-slate-700">{k.replace(/_/g, " ")}</span><span className={`font-mono text-[10px] font-bold uppercase ${v === "automatic" ? "text-emerald-600" : v === "off" ? "text-slate-400" : "text-amber-600"}`}>{v}</span></div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5" data-testid="aiwf-recent">
        <SectionTitle icon={Wallet}>Recent runs</SectionTitle>
        {d.recent.length === 0 && <div className="text-[13px] text-slate-400">No runs yet.</div>}
        <div className="divide-y divide-slate-100">
          {d.recent.map((r) => (
            <div key={r.id} className="flex items-start justify-between gap-3 py-2.5" data-testid="aiwf-run-row">
              <div className="min-w-0">
                <div className="truncate text-[13px] text-slate-800"><b>{r.agent}</b> · {r.task}</div>
                <div className="font-mono text-[10px] text-[#1a5fd0]">{r.model}{r.auto ? " · auto" : ""}{r.mission_id ? " · mission" : ""}</div>
              </div>
              <span className="shrink-0 font-mono text-[10px] text-slate-400">{String(r.at).slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      </Card>
      <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{d.note}</p>
    </div>
  );
}
